import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { API } from './endpoints';
import {
  StockMovementBase,
  MoveStock,
  ReplaceStock,
  ReserveStock,
  WriteOffStock,
  InventoryBalance,
  InventoryBalanceApi,
  StockMovement,
  StockMovementWithProductName,
  StockMovementWithProductNameApi,
  AgingItem,
  AgingApiItem,
} from './dto';

// Queries
const normalizeBalance = (
  balance: InventoryBalance | InventoryBalanceApi
): InventoryBalance => ({
  store_id: balance.store_id,
  product_id: balance.product_id ?? null,
  stock_state: balance.stock_state,
  quantity: 'qty' in balance ? balance.qty : balance.quantity,
  last_movement_ts: balance.last_movement_ts,
});

export const useGetBalance = (params: {
  store_id: string;
  product_id?: string;
  stock_state?: string;
}) =>
  useQuery({
    queryKey: ['inventory', 'balance', params],
    queryFn: async () => {
      const { store_id, product_id, stock_state } = params;
      if (!store_id) return [] as InventoryBalance[];

      if (product_id || stock_state) {
        const { data } = await apiClient.get<
          | InventoryBalanceApi
          | InventoryBalance
          | Array<InventoryBalanceApi | InventoryBalance>
          | null
        >(
          API.BALANCE,
          { params }
        );
        if (!data) {
          return [] as InventoryBalance[];
        }
        if (Array.isArray(data)) {
          return (data as Array<InventoryBalance | InventoryBalanceApi>).map(normalizeBalance);
        }
        return [normalizeBalance(data)];
      }

      const { data } = await apiClient.get<InventoryBalanceApi[]>(
        API.STORE_PRODUCTS(store_id),
        {
          params: stock_state ? { stock_state } : undefined,
        }
      );
      return data.map(normalizeBalance);
    },
    enabled: !!params.store_id,
  });

export const useGetHistory = (productId?: string, store_id?: string) =>
  useQuery({
    queryKey: ['inventory', 'history', { productId, store_id }],
    queryFn: async () => {
      const { data } = await apiClient.get<StockMovement[]>(
        API.HISTORY(productId as string),
        {
          params: store_id ? { store_id } : undefined,
        }
      );
      return data;
    },
    enabled: !!productId,
  });

const normalizeMovementWithProductName = (
  movement: StockMovementWithProductNameApi
): StockMovementWithProductName => ({
  id: movement.id ?? movement._id ?? '',
  type: movement.type,
  store_id: movement.store_id,
  product_id: movement.product_id,
  product_name: movement.product_name ?? null,
  stock_state: movement.stock_state,
  quantity: movement.quantity,
  ts: movement.ts,
  last_movement_ts: movement.last_movement_ts ?? null,
  manual: movement.manual,
  source_doc: movement.source_doc,
});

export const fetchStockMovementsByStoreAndState = async (params: {
  storeId: string;
  status: string;
  product_id?: string;
  from?: string;
  to?: string;
  limit?: number;
}) => {
  const { storeId, status, ...query } = params;
  const { data } = await apiClient.get<StockMovementWithProductNameApi[]>(
    API.MOVEMENTS_BY_STORE_STATE(storeId, status),
    { params: query }
  );
  return data.map(normalizeMovementWithProductName);
};

const normalizeAging = (item: AgingItem | AgingApiItem): AgingItem => ({
  store_id: 'store_id' in item ? item.store_id : undefined,
  product_id: item.product_id,
  days: 'age_days' in item ? item.age_days : (item as AgingItem).days,
  bucket: item.bucket,
  stock_state: 'stock_state' in item ? item.stock_state : undefined,
  quantity: 'qty' in item ? item.qty ?? 0 : (item as AgingItem).quantity,
});

export const useGetAging = (store_id: string) =>
  useQuery({
    queryKey: ['inventory', 'aging', { store_id }],
    queryFn: async () => {
      const { data } = await apiClient.get<Array<AgingItem | AgingApiItem>>(API.AGING, {
        params: { store_id },
      });
      return data.map(normalizeAging);
    },
    enabled: !!store_id,
  });

export const useGetFlow = (storeId: string) =>
  useQuery({
    queryKey: ['inventory', 'flow', { storeId }],
    queryFn: async () => {
      const { data } = await apiClient.get(API.FLOW(storeId));
      return data as Array<{ ts: string; in: number; out: number }>;
    },
    enabled: !!storeId,
  });

// Mutations helper
const successInvalidate = (
  qc: ReturnType<typeof useQueryClient>,
  keys: unknown[]
) => {
  keys.forEach((k) => qc.invalidateQueries({ queryKey: k as any }));
};

export const useInStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StockMovementBase) =>
      apiClient.post(API.IN, payload),
    onSuccess: (_, variables) => {
      successInvalidate(qc, [
        ['inventory', 'balance', { store_id: variables.store_id }],
        ['inventory', 'history', { productId: variables.product_id }],
      ]);
    },
  });
};

export const useOutStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StockMovementBase) =>
      apiClient.post(API.OUT, payload),
    onSuccess: (_, variables) => {
      successInvalidate(qc, [
        ['inventory', 'balance', { store_id: variables.store_id }],
        ['inventory', 'history', { productId: variables.product_id }],
      ]);
    },
  });
};

export const useMoveStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: MoveStock) => apiClient.post(API.MOVE, payload),
    onSuccess: (_, variables) => {
      successInvalidate(qc, [
        ['inventory', 'balance', { store_id: variables.store_id_from }],
        ['inventory', 'balance', { store_id: variables.store_id_to }],
      ]);
    },
  });
};

export const useReplaceStock = () =>
  useMutation({
    mutationFn: (payload: ReplaceStock) => apiClient.post(API.REPLACE, payload),
  });

export const useAdjustStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StockMovementBase) =>
      apiClient.post(API.ADJUST, payload),
    onSuccess: (_, variables) => {
      successInvalidate(qc, [
        ['inventory', 'balance', { store_id: variables.store_id }],
      ]);
    },
  });
};

export const useReserveStock = () =>
  useMutation({
    mutationFn: (payload: ReserveStock) => apiClient.post(API.RESERVE, payload),
  });

export const useReleaseStock = () =>
  useMutation({
    mutationFn: (payload: ReserveStock) => apiClient.post(API.RELEASE, payload),
  });

export const useWriteOffStock = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: WriteOffStock) =>
      apiClient.post(API.WRITE_OFF, payload),
    onSuccess: (_, variables) => {
      successInvalidate(qc, [
        ['inventory', 'balance', { store_id: variables.store_id }],
      ]);
    },
  });
};
